const CHAT_WAKEUP_MARKER = '[custom-agent-wakeup]'

export interface ChatWakeupInstruction {
  runId: string
  agentName: string
  state: string
  kind?: 'chat' | 'follow_up' | 'bulk'
  parentRunId?: string
  outcome?: CustomAgentFollowUpOutcome
  bulkOutcome?: CustomAgentBulkBatchOutcome
}

export interface CustomAgentBulkBatchOutcome {
  batchId: string
  agentName: string
  state: string
  itemCount: number
  pendingCount: number
  runningCount: number
  completedCount: number
  failedCount: number
  cancelledCount: number
}

export interface CustomAgentFollowUpOutcome {
  followUpRunId: string
  parentRunId: string
  agentName: string
  state: string
  resultUpdated: boolean
  currentRevision: number
  completionStatus?: 'gone' | 'false_positive' | 'still' | 'reopened'
  completionReason?: string
  completionExplanation?: string
  parkedChangeCount: number
}

export interface CustomAgentFollowUpOutcomeDescriptor {
  key:
    | 'run_state'
    | 'findings_updated'
    | 'resolved'
    | 'false_positive'
    | 'still_happening'
    | 'reopened'
    | 'parked_changes'
    | 'unchanged'
  label: string
  summary: string
}

export function describeCustomAgentFollowUpOutcome(
  outcome: CustomAgentFollowUpOutcome
): CustomAgentFollowUpOutcomeDescriptor[] {
  if (outcome.state !== 'completed') {
    const state = outcome.state.replaceAll('_', ' ')
    return [{ key: 'run_state', label: `Run ${state}`, summary: state }]
  }
  const descriptors: CustomAgentFollowUpOutcomeDescriptor[] = []
  if (outcome.resultUpdated) {
    descriptors.push({
      key: 'findings_updated',
      label: 'Findings updated',
      summary: 'findings updated'
    })
  }
  if (outcome.completionStatus === 'gone') {
    descriptors.push({
      key: 'resolved',
      label: 'Resolved',
      summary: 'resolved'
    })
  }
  if (outcome.completionStatus === 'false_positive') {
    descriptors.push({
      key: 'false_positive',
      label: 'Marked false positive',
      summary: 'marked false positive'
    })
  }
  if (outcome.completionStatus === 'still') {
    descriptors.push({
      key: 'still_happening',
      label: 'Still happening',
      summary: 'still happening'
    })
  }
  if (outcome.completionStatus === 'reopened') {
    descriptors.push({
      key: 'reopened',
      label: 'Reopened',
      summary: 'reopened'
    })
  }
  if (outcome.parkedChangeCount > 0) {
    descriptors.push({
      key: 'parked_changes',
      label: `${outcome.parkedChangeCount} ${
        outcome.parkedChangeCount === 1 ? 'change' : 'changes'
      } awaiting review`,
      summary: 'changes awaiting review'
    })
  }
  return descriptors.length > 0
    ? descriptors
    : [
        {
          key: 'unchanged',
          label: 'No finding status changed',
          summary: 'no result state change'
        }
      ]
}

export interface ChatWakeupTurn {
  /**
   * Stored as the run prompt and the queued message. Carries the marker and
   * metadata but never the result document or error.
   */
  prompt: string
  /** What the query agent is asked: the prompt plus the result document or error. */
  modelInstruction: string
  /** Status-row label for the query step. */
  stepDescription: string
}

export function buildChatWakeupTurn(
  args: ChatWakeupInstruction & {
    error?: string
    result?: unknown
  }
): ChatWakeupTurn {
  const metadata = JSON.stringify({
    runId: args.runId,
    agentName: args.agentName,
    state: args.state,
    kind: 'chat'
  })
  if (args.state === 'completed') {
    const prompt = [
      CHAT_WAKEUP_MARKER,
      metadata,
      `The background custom agent "${args.agentName}" has completed for the user's earlier request.`
    ].join('\n')
    return {
      prompt,
      modelInstruction: [
        prompt,
        'Use the result document below to answer that original question now.',
        'Write a concise, self-contained answer. Resolve and cite supporting ontology nodes when useful.',
        'Do not invoke another custom agent and do not describe this as a new user request.',
        '',
        'Custom-agent result document:',
        JSON.stringify(args.result ?? null)
      ].join('\n'),
      stepDescription: 'Answer the earlier question'
    }
  }
  const prompt = [
    CHAT_WAKEUP_MARKER,
    metadata,
    `The background custom agent "${args.agentName}" ended with state ${JSON.stringify(args.state)}.`
  ].join('\n')
  return {
    prompt,
    modelInstruction: [
      prompt,
      `Error: ${args.error || 'No error details were supplied.'}`,
      'Tell the user briefly that the requested background analysis did not complete. Do not invoke another custom agent.'
    ].join('\n'),
    stepDescription: 'Report that the background analysis did not finish'
  }
}

export function buildFollowUpOutcomeInstruction(outcome: CustomAgentFollowUpOutcome): string {
  return [
    CHAT_WAKEUP_MARKER,
    JSON.stringify({
      runId: outcome.followUpRunId,
      agentName: outcome.agentName,
      state: outcome.state,
      kind: 'follow_up',
      parentRunId: outcome.parentRunId,
      outcome
    }),
    'This is a persisted custom-agent follow-up outcome, not a new user request.',
    'Use it as context on the next normal user turn. Acknowledge relevant state changes, but do not quote the hidden follow-up summary verbatim.'
  ].join('\n')
}

export function buildBulkBatchOutcomeInstruction(outcome: CustomAgentBulkBatchOutcome): string {
  return [
    CHAT_WAKEUP_MARKER,
    JSON.stringify({
      runId: outcome.batchId,
      agentName: outcome.agentName,
      state: outcome.state,
      kind: 'bulk',
      bulkOutcome: outcome
    }),
    'This is a persisted custom-agent bulk batch outcome, not a new user request.',
    'Use it as context on the next normal user turn.'
  ].join('\n')
}

export function parseChatWakeupInstruction(value: unknown): ChatWakeupInstruction | null {
  if (typeof value !== 'string') return null
  const [marker, metadata] = value.split('\n', 2)
  if (marker !== CHAT_WAKEUP_MARKER || !metadata) return null
  try {
    const parsed = JSON.parse(metadata) as Partial<ChatWakeupInstruction>
    return typeof parsed.runId === 'string' &&
      parsed.runId.length > 0 &&
      typeof parsed.agentName === 'string' &&
      parsed.agentName.length > 0 &&
      typeof parsed.state === 'string' &&
      parsed.state.length > 0
      ? {
          runId: parsed.runId,
          agentName: parsed.agentName,
          state: parsed.state,
          ...(parsed.kind === 'chat' || parsed.kind === 'follow_up' || parsed.kind === 'bulk'
            ? { kind: parsed.kind }
            : {}),
          ...(typeof parsed.parentRunId === 'string' && parsed.parentRunId.length > 0
            ? { parentRunId: parsed.parentRunId }
            : {}),
          ...(isFollowUpOutcome(parsed.outcome) ? { outcome: parsed.outcome } : {}),
          ...(isBulkBatchOutcome(parsed.bulkOutcome) ? { bulkOutcome: parsed.bulkOutcome } : {})
        }
      : null
  } catch {
    return null
  }
}

function isBulkBatchOutcome(value: unknown): value is CustomAgentBulkBatchOutcome {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
  const outcome = value as Partial<CustomAgentBulkBatchOutcome>
  return (
    typeof outcome.batchId === 'string' &&
    outcome.batchId.length > 0 &&
    typeof outcome.agentName === 'string' &&
    typeof outcome.state === 'string' &&
    typeof outcome.itemCount === 'number' &&
    typeof outcome.pendingCount === 'number' &&
    typeof outcome.runningCount === 'number' &&
    typeof outcome.completedCount === 'number' &&
    typeof outcome.failedCount === 'number' &&
    typeof outcome.cancelledCount === 'number'
  )
}

function isFollowUpOutcome(value: unknown): value is CustomAgentFollowUpOutcome {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
  const outcome = value as Partial<CustomAgentFollowUpOutcome>
  return (
    typeof outcome.followUpRunId === 'string' &&
    outcome.followUpRunId.length > 0 &&
    typeof outcome.parentRunId === 'string' &&
    outcome.parentRunId.length > 0 &&
    typeof outcome.agentName === 'string' &&
    typeof outcome.state === 'string' &&
    typeof outcome.resultUpdated === 'boolean' &&
    typeof outcome.currentRevision === 'number' &&
    typeof outcome.parkedChangeCount === 'number'
  )
}
